import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  JobState,
  RingCentralAutoAttendantReportEntry,
  RingCentralExportDetails,
} from '../../../shared/models/generated/ziroModels';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { DateAgoPipe } from '../../../shared/pipes/date-ago.pipe';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import {
  DatatableColumn,
  DatatableRow,
  DatatableRowActionButton,
} from '../../../shared/tables/datatable/datatable.models';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';
import { RingCentralAutoAttendantsContext } from '../../services/ring-central/ring-central-auto-attendants.context';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';

interface AutoAttendantRow extends DatatableRow {
  name: string;
  phoneNumbers: string;
}

@Component({
  selector: 'ziro-ring-central-auto-attendants-list',
  templateUrl: './ring-central-auto-attendants-list.component.html',
  styleUrls: ['../../migrations.component.scss'],
  standalone: false,
})
export class RingCentralAutoAttendantsListComponent implements OnInit, OnDestroy {
  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _dateAgoPipe = inject(DateAgoPipe);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _ringCentralAutoAttendantsContext = inject(RingCentralAutoAttendantsContext);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _toastService = inject(ToastService);

  private _tenantId: string;
  private _exportId: string;
  private _subscriptions = new Subscription();

  isLoading = true;
  showCustomAlert = false;
  tableDataAutomation = 'ring-central-auto-attendants-datatable';

  columns: DatatableColumn<AutoAttendantRow>[] = [
    {
      name: 'name',
      label: 'tkey;migrations.ring_central.auto_attendants_report.table.name',
      columnCharacterWidth: 40,
    },
    {
      name: 'phoneNumbers',
      label: 'tkey;migrations.ring_central.auto_attendants_report.table.phone_numbers',
      columnCharacterWidth: 35,
    },
  ];
  tableRows: AutoAttendantRow[] = [];

  ngOnInit(): void {
    ({ tenantId: this._tenantId, id: this._exportId } = this._route.snapshot.params);

    this._initBottomNav();

    const assessmentsSub = this._migrationsAssessmentsContext.state$.subscribe((assessments) => {
      const ringExport = assessments.ringCentralExports.find(
        (ringCentralExport: RingCentralExportDetails) => ringCentralExport.exportId === this._exportId,
      );
      this._initBreadcrumbs(ringExport.name);
    });
    this._subscriptions.add(assessmentsSub);

    const autoAttendantsSub = this._ringCentralAutoAttendantsContext.state$.subscribe((entries) => {
      this.tableRows = this._mapEntriesToRows(entries);
      this.isLoading = false;
      this.showCustomAlert = false;
    });
    this._subscriptions.add(autoAttendantsSub);

    const jobStateSub = this._ringCentralAutoAttendantsContext.jobState$.subscribe((status) => {
      if (status.lastRunTime) {
        this._updateBottomNavTimestamp(status.lastRunTime);
      }

      if (status.error && status.jobState === JobState.IDLE) {
        this.showCustomAlert = true;
        this.isLoading = false;
      }

      const isRefreshing = !status.lastRunTime || status.jobState !== JobState.IDLE;
      this._setRefreshButtonPendingState(isRefreshing);
    });
    this._subscriptions.add(jobStateSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: null,
      }),
    );
  }

  onRefreshClick(): void {
    this.isLoading = true;
    this.showCustomAlert = false;
    this._ringCentralAutoAttendantsContext.refresh().subscribe({
      next: () => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.REFRESH,
          'tkey;migrations.ring_central.auto_attendants_report.refresh_toast.title',
          'tkey;migrations.ring_central.auto_attendants_report.refresh_toast.message',
        );
      },
    });
  }

  private _mapEntriesToRows(entries: RingCentralAutoAttendantReportEntry[]): AutoAttendantRow[] {
    return entries
      .map((entry) => {
        const action: DatatableRowActionButton<AutoAttendantRow> = {
          dataAutomation: 'view-auto-attendant-button',
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.PRIMARY,
          icon: ZiroIcons.NEXT,
          disabled: false,
          onClick: () => this._router.navigate([entry.id], { relativeTo: this._route }),
        };
        return {
          name: entry.name,
          phoneNumbers: entry.autoAttendantDirectNumbers
            .map((directNumber) => this._telephoneNumberFilter.transform(directNumber.number))
            .join(', '),
          actions: [action],
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private _initBreadcrumbs(exportName: string): void {
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: exportName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/ring-central/${this._exportId}`,
      },
      { label: 'tkey;migrations.ring_central.auto_attendants_report.breadcrumb' },
    ]);
  }

  private _updateBottomNavTimestamp(timestamp: string): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: `
          <i class="${ZiroIcons.TIMESTAMP}"></i>
          ${this._translateService.instant('tkey;migrations.ring_central.auto_attendants_report.last_updated')}
          <strong>${this._dateAgoPipe.transform(timestamp)}</strong>
        `,
      }),
    );
  }

  private _setRefreshButtonPendingState(state: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'ring-central-auto-attendants-refresh',
        state: {
          pending: state,
          buttonDisableState: { disabled: state, tooltipKey: '' },
        },
      }),
    );
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'ring-central-auto-attendants-back',
          dataAutomation: 'ring-central-auto-attendants-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => this._router.navigate(['..'], { relativeTo: this._route }),
        },
        {
          id: 'ring-central-auto-attendants-refresh',
          dataAutomation: 'ring-central-auto-attendants-refresh-button',
          label: 'tkey;migrations.cisco.refresh_button.label',
          icon: ZiroIcons.REFRESH,
          buttonClass: ButtonStyles.PRIMARY,
          state: {
            pending: false,
            buttonDisableState: { disabled: false },
          },
          cb: () => this.onRefreshClick(),
        },
      ]),
    );
  }
}
